import { GetStaticProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import React, { useState } from "react";
import Layout from "../../../components/layout";
import Link from "../../../components/Link";
import SubscribePopUp from "../../../components/sections/subscribe-pop-up";
import CallToActionSmall from "../../../components/v2/call-to-action-sm";
import { MwSmallIcon, NoData } from "../../../components/v2/icons";
import nextI18NextConfig from "../../../next-i18next.config";
import "../../../styles/scss/resources.module.scss";


export default function MediaCoverageNews() {
  const { t, i18n } = useTranslation('common');
  const mediaCoverageData = t('resources.mediaCoverage', { returnObjects: true, defaultValue: {} }) as any;

  // Always get English image paths to avoid broken images
  const englishMediaCoverageData = i18n.language !== 'en'
    ? (i18n.getResourceBundle('en', 'common') as any)?.resources?.mediaCoverage || {}
    : mediaCoverageData;

  const heroData = mediaCoverageData?.hero || {};
  const sidebarData = mediaCoverageData?.sidebar || {};
  const contentData = mediaCoverageData?.content || {};

  // Merge: use translated titles but English image paths and links
  const translatedItems = Array.isArray(mediaCoverageData?.items) ? mediaCoverageData.items : [];
  const englishItems = Array.isArray(englishMediaCoverageData?.items) ? englishMediaCoverageData.items : [];

const eventData = React.useMemo(() => {
  return translatedItems.map((item: any, index: number) => ({
    ...item,
    image: englishItems[index]?.image || item.image,
    link: englishItems[index]?.link || item.link
  }));
}, [translatedItems, englishItems]);

  // Use English link paths for sidebar navigation
const sidebarNavLinks = sidebarData?.navLinks || [];


  const [searchQuery, setSearchQuery] = useState("");
const filteredData = React.useMemo(() => {
  return eventData.filter((event: any) =>
    String(event?.title ?? '')
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );
}, [eventData, searchQuery]);

  const [visibleCount, setVisibleCount] = useState(6);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setVisibleCount(6);
  };

  const handleViewMore = () => {
    setVisibleCount((prev) => Math.min(prev + 6, filteredData.length));
  };
  
  return (
    <>
      <Layout
        isHomePage={false}
        pageTitle={String(mediaCoverageData?.pageTitle ?? '')}
        ogImage={String(mediaCoverageData?.ogImage ?? '')}
        uri={String(mediaCoverageData?.uri ?? '')}
        metaTitle={String(mediaCoverageData?.metaTitle ?? '')}
        metaDesc={String(mediaCoverageData?.metaDesc ?? '')}
        metaRobotsNoindex={String(mediaCoverageData?.metaRobotsNoindex ?? '')}
        metaRobotsNofollow={String(mediaCoverageData?.metaRobotsNofollow ?? '')}
      >
        <div className={"middle-wrapper site-page resources-page"}>
          <section className={"resource-hero"}>
            <div className={"container"}>
              <div className={"d-flex"}>
                <div className={"left-block"}>
                  <h1>
                    {String(heroData?.titlePrefix ?? '')}{" "}
                    <span className={"text-gradient"}>{String(heroData?.titleHighlight ?? '')}</span>
                  </h1>
                  <p>{String(heroData?.description ?? '')}</p>
                  <SubscribePopUp />
                </div>
                <div className={"right-block"}>
                  <img
                    src={"/images/resources/whitepaper-hero.png"}
                    alt={"resource-hero"}
                    width={"100%"}
                    height={"100%"}
                  />
                </div>
              </div>
            </div>
          </section>
          <section className={"main-resources-whitepaper"}>
            <div className={"container"}>
              <div className={"d-flex"}>
                <div className={"left-block"}>
                  <div className={"left-resource"}>
                    <div className={"section-tag testimonial"}>
                      <MwSmallIcon /> {String(sidebarData?.sectionTag ?? '')}
                    </div>
                    <h2>{String(sidebarData?.title ?? '')}</h2>
                    <input
                      type={"search"}
                      placeholder={String(sidebarData?.searchPlaceholder ?? '')}
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                    <ul>
                      {sidebarNavLinks.map((link: any, index: number) => (
                        <Link key={index} href={String(link?.link ?? '')}>
                          <li className={link?.active === true ? "active" : ""}>{String(link?.text ?? '')}</li>
                        </Link>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className={"right-block"}>
                  <div className={"content"}>
                    <div className={"d-flex"}>
                      {filteredData.length > 0 ? (
                        filteredData.slice(0, visibleCount).map((data: any, index: number) => (
                          <div className={"inner-block"} key={index}>
                            <div className={"image-wrapper"}>
                              <img
                                src={String(data?.image ?? '')}
                                alt={String(data?.title ?? '')}
                                width={"100%"}
                                height={"93%"}
                              />
                            </div>

                            <div className={"category knowledge"}>
                              {String(contentData?.categoryLabel ?? '')}
                            </div>
                            <Link
                              href={String(data?.link ?? '')}
                              itemProp="url"
                              target={"_blank"}
                            >
                              <h3 className={"font-inter"}>{String(data?.title ?? '')}</h3>
                            </Link>
                            <Link
                              href={String(data?.link ?? '')}
                              itemProp="url"
                              target={"_blank"}
                              className={"read-more"}
                            >
                              {String(contentData?.learnMoreText ?? '')}
                            </Link>
                          </div>
                        ))
                      ) : (
                        <div className="no-results">
                          <NoData />
                          <p dangerouslySetInnerHTML={{ __html: String(contentData?.emptyState?.text ?? '') }}></p>
                        </div>
                      )}
                    </div>
                    {visibleCount < filteredData.length && (
                      <div className={"button-shadow-view"}> <button className="view-more-btn" onClick={handleViewMore}>
                        {String(contentData?.viewMoreText ?? '')}
                      </button></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
          <CallToActionSmall />
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translations = await serverSideTranslations(
    ctx?.locale ?? "en",
    ["common", "header", "footer"],
    nextI18NextConfig,
  );
  return {
    props: {
      pageData: "",
      pageTitle: "Middleware in the news!",
      description:
        "Stay updated on Middleware's latest media coverage, expert viewpoints, and industry trends.",
      uri: "/resources/media-coverage/",
      metaTitle: "Middleware In The News!",
      metaDesc:
        "Stay updated on Middleware's latest media coverage, expert viewpoints, and industry trends.",
      metaRobotsNoindex: "index",
      metaRobotsNofollow: "follow",
      ...translations,
    },
  };
};